/**
 * followUpsLogic.js
 *
 * Pure logic for the follow-up feature. Two independent tracks:
 *   - notResponded: fires `delay` (unit-aware) after the main DM send, IF
 *     the user still hasn't clicked the CTA by then.
 *   - responded:    fires `delay` after the user's click event.
 *
 * Each track stores `{ delay: number, delayUnit: "seconds"|"minutes"|"hours" }`.
 * Per-unit floors (UX foot-gun prevention):
 *   seconds  >= 10    minutes >= 1    hours >= 1
 * Combined hard max across all units: 7 days (604800000 ms).
 *
 * Legacy docs that only carry `delayHours` are migrated on READ via
 * `migrateLegacyTrack`; we never mutate the stored doc on write.
 *
 * Kept free of React/antd imports for unit-testability; helpers from
 * ../../utils are inlined because utils.js pulls in Firebase.
 */

const isStringNullOrUndefined = (obj) =>
  obj === null || obj === undefined || obj.length <= 0;

const isValidURL = (url) => {
  try {
    // eslint-disable-next-line no-new
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/* ------------------------------ Units -------------------------------- */

export const DELAY_UNITS = ["seconds", "minutes", "hours"];

const UNIT_MS = {
  seconds: 1000,
  minutes: 60 * 1000,
  hours: 60 * 60 * 1000,
};

/** Per-unit minimum. 10s floor prevents follow-ups racing the main DM's send. */
export const UNIT_MIN = {
  seconds: 10,
  minutes: 1,
  hours: 1,
};

/** Hard max across all units: 7 days. */
export const MAX_DELAY_MS = 7 * 24 * 60 * 60 * 1000;

export const UNIT_MAX = {
  seconds: MAX_DELAY_MS / UNIT_MS.seconds, // 604800
  minutes: MAX_DELAY_MS / UNIT_MS.minutes, // 10080
  hours: MAX_DELAY_MS / UNIT_MS.hours, //      168
};

/** Convert `{ delay, delayUnit }` to milliseconds. */
export const delayToMs = (delay, delayUnit) => {
  const n = Number(delay);
  if (!Number.isFinite(n) || n <= 0) return 0;
  const per = UNIT_MS[delayUnit] || UNIT_MS.hours;
  return n * per;
};

/* ----------------------------- Defaults ------------------------------ */

export const DEFAULT_TRACK = () => ({
  enabled: false,
  delay: 2,
  delayUnit: "hours",
  message: "",
  imageUrl: "",
  buttonText: "",
  link: "",
});

export const DEFAULT_FOLLOW_UPS = () => ({
  notResponded: DEFAULT_TRACK(),
  responded: DEFAULT_TRACK(),
});

/* ------------------------ Legacy-shape migration --------------------- */

/**
 * Read-path migrator: a track saved under the old shape (`delayHours: X`
 * with no `delay`/`delayUnit`) is normalized to the new shape. Never
 * mutates; returns a new object.
 */
export const migrateLegacyTrack = (track) => {
  if (!track || typeof track !== "object") return track;
  if (track.delay !== undefined || track.delayUnit !== undefined) return track;
  if (track.delayHours !== undefined) {
    return {
      ...track,
      delay: Number(track.delayHours) || DEFAULT_TRACK().delay,
      delayUnit: "hours",
    };
  }
  return track;
};

/** Normalize the whole followUps object. Safe on null/undefined. */
export const migrateLegacyFollowUps = (followUps) => {
  if (!followUps) return followUps;
  return {
    ...followUps,
    notResponded: migrateLegacyTrack(followUps.notResponded),
    responded: migrateLegacyTrack(followUps.responded),
  };
};

/* ---------------------------- Validation ----------------------------- */

const validateTrack = (track, trackKey) => {
  const errors = {};
  if (!track?.enabled) return errors;

  const unit = DELAY_UNITS.includes(track.delayUnit) ? track.delayUnit : "hours";
  const n = Number(track.delay);
  const min = UNIT_MIN[unit];
  const max = UNIT_MAX[unit];
  if (!Number.isFinite(n) || n < min || n > max) {
    errors[`${trackKey}_delay`] =
      `Delay must be between ${min} and ${max} ${unit}.`;
  }
  if (!DELAY_UNITS.includes(track.delayUnit)) {
    errors[`${trackKey}_delayUnit`] = "Choose a time unit.";
  }

  const textMissing = isStringNullOrUndefined(track.message);
  const hasBtnLabel = !isStringNullOrUndefined(track.buttonText);
  const hasBtnLink = !isStringNullOrUndefined(track.link);

  if (textMissing && !(hasBtnLabel && hasBtnLink)) {
    errors[`${trackKey}_message`] =
      "Add a message or a complete CTA button.";
  }
  if (hasBtnLabel && !hasBtnLink) {
    errors[`${trackKey}_link`] = "Enter a URL for the button.";
  }
  if (hasBtnLink && !isValidURL(track.link)) {
    errors[`${trackKey}_link`] = "Enter a valid URL.";
  }
  if (hasBtnLink && !hasBtnLabel) {
    errors[`${trackKey}_buttonText`] = "Add a button label.";
  }
  return errors;
};

/** Validate both tracks. Caller should gate on hasCTA before calling. */
export const validateFollowUps = (followUps) => {
  const migrated = migrateLegacyFollowUps(followUps);
  if (!migrated) return {};
  return {
    ...validateTrack(migrated.notResponded, "notResponded"),
    ...validateTrack(migrated.responded, "responded"),
  };
};

/* -------------------------- Preview helpers -------------------------- */

export const buildTrackChat = (track) => {
  if (!track?.enabled) return null;
  const hasText = !isStringNullOrUndefined(track.message);
  const hasBtn =
    !isStringNullOrUndefined(track.buttonText) &&
    !isStringNullOrUndefined(track.link);
  if (!hasText && !hasBtn) return null;
  const chat = { isClient: true, message: track.message || "" };
  if (track.imageUrl) chat.imageUrl = track.imageUrl;
  if (hasBtn) chat.buttons = [{ buttonText: track.buttonText }];
  return chat;
};

const formatDelay = (track) => {
  const t = migrateLegacyTrack(track);
  const unit = DELAY_UNITS.includes(t?.delayUnit) ? t.delayUnit : "hours";
  const n = Number(t?.delay) || "?";
  return `${n} ${unit}`;
};

export const appendFollowUpChats = (chats, followUps) => {
  const migrated = migrateLegacyFollowUps(followUps);
  if (!migrated) return chats;

  const nr = buildTrackChat(migrated.notResponded);
  if (nr) {
    chats.push({
      isDivider: true,
      message: `+${formatDelay(migrated.notResponded)} after main DM · if user didn't respond`,
    });
    chats.push(nr);
  }

  const r = buildTrackChat(migrated.responded);
  if (r) {
    chats.push({
      isDivider: true,
      message: `+${formatDelay(migrated.responded)} after user clicks · if user responded`,
    });
    chats.push(r);
  }

  return chats;
};
